import axios from 'axios';

interface ApiError {
  message: string;
  status?: number;
  code?: string;
}

const API_URL = import.meta.env.VITE_API_URL;

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: ApiError) => void;
}> = [];

const processQueue = (error: ApiError | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

// Store IP address globally
let clientIpAddress: string | null = null;

// Function to set IP address
export const setClientIp = (ip: string) => {
  clientIpAddress = ip;
};

axios.defaults.baseURL = API_URL;

// Add proper type definitions
interface RefreshTokenResponse {
  token: string;
  refreshToken: string;
}

// Extract constants
const STORAGE_KEYS = {
  TOKEN: 'token',
  REFRESH_TOKEN: 'refreshToken',
} as const;

const AUTH_ENDPOINTS = {
  LOGIN: '/auth/login',
  REFRESH: '/auth/refresh-token',
} as const;

axios.interceptors.request.use((config) => {
  const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (clientIpAddress) {
    config.headers['X-Client-IP'] = clientIpAddress;
  }
  return config;
}, Promise.reject); // Add error handler

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.code === 'ERR_NETWORK') {
      redirectToConnectionError();
      return Promise.reject(error);
    }

    // Handle precondition failed
    if (error.response?.status === 412) {
      clearAuthStorage();
      redirectToLogin();
      return Promise.reject(error);
    }

    // Handle unauthorized
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      originalRequest.url !== AUTH_ENDPOINTS.LOGIN
    ) {
      return handleTokenRefresh(originalRequest);
    }

    return Promise.reject(error);
  }
);

// Helper functions
export function clearAuthStorage(): void {
  localStorage.removeItem(STORAGE_KEYS.TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
}

function redirectToLogin(): void {
  window.location.href = '/login';
}

function redirectToConnectionError(): void {
  if (window.location.pathname === '/connection-error') return;

  window.location.href = `/connection-error?redirect=${window.location.pathname}`;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function handleTokenRefresh(originalRequest: any) {
  if (isRefreshing) {
    return handleQueuedRequest(originalRequest);
  }

  originalRequest._retry = true;
  isRefreshing = true;

  try {
    const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const { token, refreshToken: newRefreshToken } = await refreshAuthToken(refreshToken);

    updateAuthTokens(token, newRefreshToken);
    processQueue(null, token);

    originalRequest.headers.Authorization = `Bearer ${token}`;
    return axios(originalRequest);
  } catch (refreshError) {
    processQueue(refreshError as ApiError, null);
    clearAuthStorage();
    redirectToLogin();
    return Promise.reject(refreshError);
  } finally {
    isRefreshing = false;
  }
}

async function refreshAuthToken(refreshToken: string): Promise<RefreshTokenResponse> {
  const response = await axios.post<RefreshTokenResponse>(
    AUTH_ENDPOINTS.REFRESH,
    {},
    {
      headers: { Authorization: `Bearer ${refreshToken}` },
    }
  );
  return response.data;
}

function updateAuthTokens(token: string, refreshToken: string): void {
  localStorage.setItem(STORAGE_KEYS.TOKEN, token);
  localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function handleQueuedRequest(originalRequest: any): Promise<any> {
  try {
    const token = await new Promise<string>((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
    originalRequest.headers.Authorization = `Bearer ${token}`;
    return axios(originalRequest);
  } catch (err) {
    return Promise.reject(err);
  }
}

export default axios;
